import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchAbReviewSamples,
  fetchAbReviewSample,
  submitAbReviewVerdict,
  fetchAbReviewStats,
} from '../services/api';

/**
 * A/B Review Console — PR6 of the timeline refactor.
 *
 * Super-admin only (route-gated in App.tsx). Powers the cutover-gate
 * (Q9 grilling): SME picks stratified samples, sees both pipelines
 * side-by-side, submits a verdict (append-only).
 *
 * Empty state is expected until PR8 flips shadow-mode on for a tenant.
 */

type Verdict = 'new_better' | 'legacy_better' | 'both_equivalent' | 'both_bad';

interface StagePrediction {
  should_change?: boolean;
  to_stage?: string | null;
  confidence?: number | null;
}

interface SampleSummary {
  shadow_run_id: number;
  lead_id: number;
  ran_at: string | null;
  stratum: string;
  new: StagePrediction;
  legacy: StagePrediction;
}

interface SamplesResponse {
  tenant_id: number;
  pool_size: number;
  per_stratum?: number;
  note?: string;
  strata: Record<string, SampleSummary[]>;
}

interface SampleDetail {
  id: number;
  tenant_id: number;
  lead_id: number;
  lead: { id: number; name: string; phone: string; lead_status: string | null; cumulative_summary: string | null } | null;
  ran_at: string | null;
  new_pipeline_version: string;
  new_pipeline_output: Record<string, unknown>;
  legacy_output: Record<string, unknown> | null;
  diff_summary: string | null;
  existing_verdicts: Array<{
    id: number;
    reviewer_user_id: number;
    verdict: Verdict;
    stratum: string | null;
    notes: string | null;
    created_at: string;
  }>;
}

interface StatsResponse {
  tenant_id: number;
  shadow_runs_total: number;
  unique_reviewed: number;
  verdict_counts: Record<string, number>;
  stratum_breakdown: Record<string, Record<string, number>>;
}

const card: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
  padding: 20, marginBottom: 16,
};
const label: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
};
const codeBox: React.CSSProperties = {
  background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 6,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12, lineHeight: 1.5, overflowX: 'auto', maxHeight: 400,
};
const strataOrder: Array<keyof SamplesResponse['strata']> = [
  'both_should_change_agreed', 'stage_disagreement',
  'confidence_differed', 'random',
];
const strataLabels: Record<string, string> = {
  both_should_change_agreed: 'Both agreed on stage change',
  stage_disagreement: 'Stage disagreement',
  confidence_differed: 'Confidence differed (>0.3)',
  random: 'Random',
};

const AbReviewConsolePage: React.FC = () => {
  const [tenantInput, setTenantInput] = useState<string>('10');
  const [samples, setSamples]     = useState<SamplesResponse | null>(null);
  const [samplesErr, setSamplesErr] = useState<string | null>(null);
  const [loading, setLoading]     = useState<boolean>(false);

  const [selected, setSelected]   = useState<SampleDetail | null>(null);
  const [selectedErr, setSelectedErr] = useState<string | null>(null);
  const [selectedStratum, setSelectedStratum] = useState<string | null>(null);

  const [verdict, setVerdict] = useState<Verdict>('new_better');
  const [verdictNotes, setVerdictNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitMsg, setSubmitMsg]   = useState<string | null>(null);

  const [stats, setStats]         = useState<StatsResponse | null>(null);

  const loadSamples = useCallback(async () => {
    if (!tenantInput) return;
    setLoading(true);
    setSamplesErr(null);
    try {
      const [s, st] = await Promise.all([
        fetchAbReviewSamples(Number(tenantInput)),
        fetchAbReviewStats(Number(tenantInput)),
      ]);
      setSamples(s);
      setStats(st);
    } catch (e: any) {
      setSamplesErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load samples');
    } finally {
      setLoading(false);
    }
  }, [tenantInput]);

  useEffect(() => { loadSamples(); }, [loadSamples]);

  const openSample = async (shadowRunId: number, stratum: string) => {
    setSelectedErr(null);
    setSubmitMsg(null);
    setSelected(null);
    setSelectedStratum(stratum);
    try {
      const d = await fetchAbReviewSample(shadowRunId);
      setSelected(d);
    } catch (e: any) {
      setSelectedErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load sample');
    }
  };

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const r = await submitAbReviewVerdict({
        shadow_run_id: selected.id,
        verdict,
        stratum: selectedStratum,
        notes: verdictNotes || null,
      });
      setSubmitMsg(`Verdict ${r.verdict_id} recorded (${r.verdict}).`);
      setVerdictNotes('');
      loadSamples(); // refresh stats + samples
      openSample(selected.id, selectedStratum ?? '');
    } catch (e: any) {
      setSubmitMsg(e?.response?.data?.error ?? e?.message ?? 'Failed to submit verdict');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 8 }}>A/B Review Console</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Timeline-refactor cutover gate. Review stratified shadow-mode samples,
        submit verdicts. Append-only — every verdict persists for audit.
      </p>

      <div style={card}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <div style={label}>Tenant ID</div>
            <input
              type="number"
              value={tenantInput}
              onChange={(e) => setTenantInput(e.target.value)}
              placeholder="10"
              style={{ padding: 8, width: '100%', border: '1px solid var(--border)', borderRadius: 6 }}
            />
          </div>
          <button onClick={loadSamples} disabled={loading} style={btnPrimary(loading)}>
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>
      </div>

      {samplesErr && <div style={{ ...card, color: '#dc2626' }}>{samplesErr}</div>}

      {stats && <StatsPanel stats={stats} />}

      {samples && samples.pool_size === 0 && (
        <div style={card}>
          <div style={label}>No shadow runs</div>
          <div>{samples.note ?? 'Nothing to review yet. Enable shadow mode for this tenant.'}</div>
        </div>
      )}

      {samples && samples.pool_size > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
          {/* Left: strata + samples */}
          <div>
            {strataOrder.map((s) => {
              const items = samples.strata[s] ?? [];
              return (
                <div key={s} style={card}>
                  <div style={label}>{strataLabels[s]}  ·  {items.length}</div>
                  {items.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>none</div>}
                  {items.map((it) => (
                    <button
                      key={it.shadow_run_id}
                      onClick={() => openSample(it.shadow_run_id, s)}
                      style={sampleRow(selected?.id === it.shadow_run_id)}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        Run #{it.shadow_run_id} · lead {it.lead_id}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        NEW: {stageChip(it.new)} · LEGACY: {stageChip(it.legacy)}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Right: selected detail + verdict */}
          <div>
            {selectedErr && <div style={{ ...card, color: '#dc2626' }}>{selectedErr}</div>}
            {!selected && !selectedErr && (
              <div style={card}>
                <div style={label}>Select a sample</div>
                <div style={{ color: '#6b7280' }}>Click a run on the left to review both pipelines side by side.</div>
              </div>
            )}
            {selected && (
              <>
                <div style={card}>
                  <div style={label}>Lead</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {selected.lead?.name ?? '—'} · {selected.lead?.phone ?? ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    lead_id {selected.lead_id} · tenant {selected.tenant_id} · ran {selected.ran_at}
                  </div>
                  {selected.lead?.cumulative_summary && (
                    <div style={{ marginTop: 12, fontSize: 13 }}>
                      <div style={label}>Current summary</div>
                      {selected.lead.cumulative_summary}
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={card}>
                    <div style={label}>New pipeline ({selected.new_pipeline_version})</div>
                    <pre style={codeBox}>{JSON.stringify(selected.new_pipeline_output, null, 2)}</pre>
                  </div>
                  <div style={card}>
                    <div style={label}>Legacy pipeline</div>
                    <pre style={codeBox}>{JSON.stringify(selected.legacy_output, null, 2)}</pre>
                  </div>
                </div>

                {selected.diff_summary && (
                  <div style={card}>
                    <div style={label}>Diff summary</div>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{selected.diff_summary}</div>
                  </div>
                )}

                <div style={card}>
                  <div style={label}>Submit verdict</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                    {(['new_better', 'legacy_better', 'both_equivalent', 'both_bad'] as Verdict[]).map((v) => (
                      <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                        <input type="radio" name="verdict" checked={verdict === v} onChange={() => setVerdict(v)} />
                        {v.replace(/_/g, ' ')}
                      </label>
                    ))}
                  </div>
                  <textarea
                    value={verdictNotes}
                    onChange={(e) => setVerdictNotes(e.target.value)}
                    placeholder="Notes (optional) — why this verdict? What did each pipeline miss/nail?"
                    rows={3}
                    style={{ width: '100%', marginTop: 12, padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}
                  />
                  <button onClick={submit} disabled={submitting} style={{ ...btnPrimary(submitting), marginTop: 12 }}>
                    {submitting ? 'Submitting…' : 'Submit verdict'}
                  </button>
                  {submitMsg && <div style={{ marginTop: 8, fontSize: 13, color: '#059669' }}>{submitMsg}</div>}
                </div>

                {selected.existing_verdicts.length > 0 && (
                  <div style={card}>
                    <div style={label}>Existing verdicts on this run · {selected.existing_verdicts.length}</div>
                    {selected.existing_verdicts.map((v) => (
                      <div key={v.id} style={{ padding: 8, borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                        <strong>{v.verdict}</strong> by user {v.reviewer_user_id} · {v.created_at}
                        {v.notes && <div style={{ color: '#6b7280', marginTop: 4 }}>{v.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatsPanel: React.FC<{ stats: StatsResponse }> = ({ stats }) => (
  <div style={card}>
    <div style={label}>Review progress</div>
    <div style={{ display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap' }}>
      <Stat name="Shadow runs" value={stats.shadow_runs_total} />
      <Stat name="Unique reviewed" value={stats.unique_reviewed} />
      {Object.entries(stats.verdict_counts).map(([k, v]) => (
        <Stat key={k} name={k.replace(/_/g, ' ')} value={v} />
      ))}
    </div>
  </div>
);

const Stat: React.FC<{ name: string; value: number }> = ({ name, value }) => (
  <div>
    <div style={label}>{name}</div>
    <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
  </div>
);

const stageChip = (p: StagePrediction): string => {
  if (!p.should_change) return 'no change';
  const c = p.confidence != null ? ` (${(p.confidence * 100).toFixed(0)}%)` : '';
  return `${p.to_stage ?? '?'}${c}`;
};

const btnPrimary = (busy: boolean): React.CSSProperties => ({
  padding: '8px 16px', background: busy ? '#94a3b8' : '#2563eb',
  color: '#fff', border: 'none', borderRadius: 6,
  cursor: busy ? 'default' : 'pointer', fontSize: 14, fontWeight: 600,
});

const sampleRow = (active: boolean): React.CSSProperties => ({
  display: 'block', textAlign: 'left', width: '100%',
  padding: 10, marginTop: 6,
  background: active ? '#eff6ff' : 'transparent',
  border: '1px solid ' + (active ? '#3b82f6' : 'var(--border)'),
  borderRadius: 6, cursor: 'pointer',
});

export default AbReviewConsolePage;
